import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Languages } from 'lucide-react';
import type { SupportedLanguage } from '@/i18n';
import { useAuth } from '@/hooks/useAuth';
import { updateSettings } from '@/lib/api/settings';
import IT from 'country-flag-icons/react/3x2/IT';
import GB from 'country-flag-icons/react/3x2/GB';

const languages = [
  { code: 'it' as SupportedLanguage, label: 'Italiano', flag: IT },
  { code: 'en' as SupportedLanguage, label: 'English', flag: GB },
];

const FlagIcon = ({ FlagComponent }: { FlagComponent: React.ComponentType<{ className?: string }> }) => (
  <FlagComponent className="w-5 h-4 inline-block rounded-sm" />
);

export function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const { user } = useAuth();

  const currentLanguage = languages.find((lang) => lang.code === i18n.language) || languages[0];

  const changeLanguage = (langCode: SupportedLanguage) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('i18nextLng', langCode);
    // Persist to DB (fire-and-forget) so settings stay in sync
    if (user?.id) {
      updateSettings(user.id, { ui_language: langCode }).catch(console.error);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2">
          <Languages className="h-4 w-4" />
          <span className="hidden sm:flex items-center gap-2">
            <FlagIcon FlagComponent={currentLanguage.flag} />
            {currentLanguage.label}
          </span>
          <span className="sm:hidden">
            <FlagIcon FlagComponent={currentLanguage.flag} />
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            className={i18n.language === lang.code ? 'bg-accent' : ''}
          >
            <span className="mr-2">
              <FlagIcon FlagComponent={lang.flag} />
            </span>
            {lang.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
